type RouteSkeletonVariant = 'list' | 'detail' | 'form' | 'onboarding';

interface RouteSkeletonProps {
  variant?: RouteSkeletonVariant;
}

const HeaderSkeleton = () => (
  <div className="space-y-3 px-page-x pt-8">
    <div className="th-skeleton h-4 w-28" />
    <div className="th-skeleton h-8 w-64 max-w-full" />
    <div className="th-skeleton h-4 w-96 max-w-full" />
  </div>
);

const ListBodySkeleton = () => (
  <div className="px-page-x pb-8 pt-8">
    <div className="rounded-md border border-border bg-card p-4">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="th-skeleton h-10 w-64 max-w-full rounded-md" />
        <div className="th-skeleton h-10 w-28 rounded-md" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="grid grid-cols-12 gap-3">
            <div className="th-skeleton col-span-5 h-4" />
            <div className="th-skeleton col-span-3 h-4" />
            <div className="th-skeleton col-span-2 h-4" />
            <div className="th-skeleton col-span-2 h-4" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const FormBodySkeleton = () => (
  <div className="px-page-x pb-8 pt-8">
    <div className="mx-auto w-full max-w-720px space-y-6">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="space-y-2">
          <div className="th-skeleton h-4 w-36" />
          <div className="th-skeleton h-10 w-full rounded-md" />
        </div>
      ))}
      <div className="th-skeleton h-28 w-full rounded-md" />
      <div className="flex justify-end">
        <div className="th-skeleton h-10 w-32 rounded-md" />
      </div>
    </div>
  </div>
);

const DetailBodySkeleton = () => (
  <div className="px-page-x pb-8 pt-8">
    <div className="rounded-md border border-border bg-card p-4">
      <div className="mb-4 flex justify-end">
        <div className="th-skeleton h-10 w-32 rounded-md" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="grid grid-cols-12 gap-3">
            <div className="th-skeleton col-span-6 h-4" />
            <div className="th-skeleton col-span-3 h-4" />
            <div className="th-skeleton col-span-3 h-4" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const OnboardingSkeleton = () => (
  <div
    className="th w-screen min-h-screen bg-background flex flex-col items-center justify-center px-14 pt-24 pb-20"
    data-testid="route-skeleton-onboarding"
  >
    <div className="w-full max-w-[520px] space-y-8">
      <div className="space-y-3">
        <div className="th-skeleton h-8 w-72 max-w-full" />
        <div className="th-skeleton h-4 w-56 max-w-full" />
      </div>
      <div className="th-skeleton h-14 w-full rounded-full" />
      <div className="flex items-center justify-between">
        <div className="th-skeleton h-4 w-24" />
        <div className="th-skeleton h-10 w-28 rounded-md" />
      </div>
    </div>
  </div>
);

const RouteSkeleton = ({ variant = 'list' }: RouteSkeletonProps) => {
  if (variant === 'onboarding') return <OnboardingSkeleton />;

  return (
    <div className="th flex h-full min-h-0 flex-col" data-testid={`route-skeleton-${variant}`}>
      <HeaderSkeleton />
      {variant === 'list' ? <ListBodySkeleton /> : null}
      {variant === 'form' ? <FormBodySkeleton /> : null}
      {variant === 'detail' ? <DetailBodySkeleton /> : null}
    </div>
  );
};

export type { RouteSkeletonVariant };
export default RouteSkeleton;
